/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState, useRef, type RefObject } from 'react';
import clsx from 'clsx';
import Glide from '@glidejs/glide';

import { VanilaCardCarouselContainerProps } from './StoryTilesContainer.types';
import { StyledCardCarousel } from './VanilaCardCarouselContainer.styles';

import { Eyebrow, Heading, Text, headingType, size, tags, Button } from '@marriott/mi-ui-library';

export const VanilaCardCarouselContainer: FC<VanilaCardCarouselContainerProps> = props => {
  const {
    componentId,
    subHeaderText,
    styleclass,
    variation,
    customAttributes,
    eyebrow,
    cardCount,
    trackingProperties,
    children,
    ctaLabel,
    headerText,
    ctaLink,
    openInaNewTab,
    ctaType,
    isCombo,
    noOfCards,
    noOfCardsTablet,
    enableTabletBreakpoint,
    isDirectionRightToLeft,
    isCobrandVariant,
    comboHeaderTag,
    standardHeaderTag,
  } = props;
  const isLink = ctaType === 'tertiaryLink';
  const [cardsPerView, setCardsPerView] = useState(noOfCards);

  const header = () =>
    headerText?.trim().length ? (
      <Heading
        customClass={clsx({ 'mt-2 mb-3': isCombo() }, isCombo() ? 'm-ellipsis-3lines' : 'lt-title-3')}
        titleText={headerText}
        variation={headingType.title}
        fontSize={isCombo() ? size.medium : size.small}
        element={isCombo() ? comboHeaderTag : standardHeaderTag}
      />
    ) : null;
  const subHeader = () =>
    subHeaderText && (
      <Text
        copyText={subHeaderText}
        fontSize={size.medium}
        element={tags.span}
        customClass={clsx({ 'mb-3': isCombo() || eyebrow }, isCombo() ? 'm-ellipsis-3lines' : 'm-ellipsis-2lines')}
      />
    );

  const cta = () => {
    return (
      ctaLabel && (
        <div className={clsx(!eyebrow && !subHeaderText ? 'carousal_header--cta mb-2' : 'carousal_header--cta')}>
          <Button
            href={ctaLink}
            isLink={isLink}
            target={openInaNewTab ? '_blank' : '_self'}
            callback={!isLink ? buttonCallback : undefined}
            className={[
              'custom_click_track',
              ctaType === 'primaryButton' ? 'm-button-primary' : '',
              ctaType === 'secondaryButton' ? 'm-button-secondary' : '',
              ctaType === 'tertiaryLink' ? 'm-link-tertiary-button' : '',
              openInaNewTab
                ? ctaType === 'primaryButton' || ctaType === 'secondaryButton'
                  ? 'm-button-external'
                  : 'm-link-tertiary-button-external'
                : '',
            ]}
            custom_click_track_value={`${trackingProperties?.trackingContentPosition}|${
              trackingProperties?.trackingDescription
            }|${openInaNewTab ? 'external' : 'internal'}`}
            buttonCopy={ctaLabel}
            isTextBeforeChildren={false}
          />
        </div>
      )
    );
  };

  const carouselHeader = () => {
    return (
      <div className={clsx('carousal_header')}>
        <div className={clsx({ 'pb-2': eyebrow }, 'container p-0')}>
          {eyebrowContent()}
          {header()}
          {subHeader()}
        </div>
        {cta()}
      </div>
    );
  };

  const carouselComboHeader = () => {
    return (
      <div className={clsx({ 'pr-5': isCombo() }, 'carousal_header')}>
        <div>
          {eyebrowContent()}
          {header()}
          {subHeader()}
        </div>
        {cta()}
      </div>
    );
  };

  const eyebrowContent = () => eyebrow && <Eyebrow text={eyebrow} />;

  const buttonCallback = () => {
    window.open(ctaLink, openInaNewTab ? '_blank' : '_self');
  };

  const leftArrowRef = useRef<HTMLButtonElement>(null);
  const rightArrowRef = useRef<HTMLButtonElement>(null);
  const leftArrowClickTrackRef = useRef<HTMLInputElement>(null);
  const rightArrowClickTrackRef = useRef<HTMLInputElement>(null);

  const ArrowDisabler = function (Glide: any, Components: any) {
    return {
      mount() {
        // Only in effect when rewinding is disabled
        if (Glide.settings.rewind) {
          return;
        }
        Glide.on(['mount.after', 'run'], () => {
          // Filter out arrows_control
          for (const controlItem of Components.Controls.items) {
            if (controlItem.className !== 'glide__arrows') {
              continue;
            }

            // Set left arrow state
            const left = controlItem?.querySelector('.glide__arrow--left');
            if (left) {
              if (Glide.index === 0) {
                left.classList.add('is-disabled'); // Disable on first slide
              } else {
                left.classList.remove('is-disabled'); // Enable on other slides
              }
            }

            // Set right arrow state
            const right = controlItem?.querySelector('.glide__arrow--right');
            if (right) {
              if (Glide.index === Components.Sizes.length - Glide.settings.perView) {
                right.classList.add('is-disabled'); // Disable on last slide
              } else {
                right.classList.remove('is-disabled'); // Disable on other slides
              }
            }
            // Adjust arrow direction for RTL
            if (isDirectionRightToLeft) {
              if (left) {
                left.classList.remove('glide__arrow--disabled');
              }
              if (right) {
                right.classList.remove('glide__arrow--disabled');
              }
            }
          }
        });
      },
    };
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const handleResize = () => {
    const xs = window.matchMedia('(max-width: 575px)');
    const sm = window.matchMedia('(min-width:576px) and (max-width: 767px)');
    const md = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
    const xl = window.matchMedia('(min-width: 1200px)');
    if (enableTabletBreakpoint) {
      if (xl?.matches) {
        setCardsPerView(noOfCards);
      } else if (md?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (sm?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (xs?.matches) {
        setCardsPerView(1);
      }
    } else {
      if (xs?.matches) {
        setCardsPerView(1);
      } else if (sm?.matches) {
        setCardsPerView(1);
      } else if (md?.matches) {
        setCardsPerView(noOfCardsTablet);
      } else if (xl?.matches) {
        setCardsPerView(noOfCards);
      }
    }
  };

  useEffect(() => {
    const carouselRootSelector = `#${componentId}`;
    if (document?.querySelector(carouselRootSelector)) {
      try {
        const glide = new Glide(carouselRootSelector, {
          type: 'slider',
          bound: true,
          perView: noOfCards,
          startAt: 0,
          rewind: false,
          direction: isDirectionRightToLeft ? 'rtl' : 'ltr',
          gap: 27,
          peek: { before: 0, after: 30 },
          dragThreshold: false,
          breakpoints: enableTabletBreakpoint
            ? {
                1199: {
                  perView: noOfCardsTablet,
                },
                767: {
                  perView: noOfCardsTablet,
                  peek: { before: 0, after: 50 },
                },
                575: {
                  perView: 1,
                  peek: { before: 0, after: 50 },
                },
              }
            : {
                1199: {
                  perView: noOfCardsTablet,
                },
                767: {
                  perView: 1,
                  peek: { before: 0, after: 50 },
                },
              },
        }).mount({ ArrowDisabler });

        const observer = new MutationObserver(() => glide.update({}));
        const sliderRootNode = document.querySelector(`${carouselRootSelector} .glide__track ul`);
        sliderRootNode && observer.observe(sliderRootNode, { childList: true });

        const cleanupFns = [
          addClickTrackListeners(leftArrowRef, leftArrowClickTrackRef),
          addClickTrackListeners(rightArrowRef, rightArrowClickTrackRef),
        ];

        return () => {
          cleanupFns.forEach(fn => fn());
          observer.disconnect();
        };
      } catch (error) {
        // log.error(`Error in initializing Glide for ${componentId}`, error);
      }
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, cardsPerView, isDirectionRightToLeft]);

  const addClickTrackListeners = (
    origRef: RefObject<HTMLElement>,
    clickTrackRef: RefObject<HTMLElement>
  ): (() => void) => {
    const handler = (e: Event) => {
      clickTrackRef.current?.click();
      e.preventDefault();
    };

    origRef.current?.addEventListener('touchstart', handler);
    origRef.current?.addEventListener('click', handler);

    // on unmount, cleanup same handler reference
    return () => {
      origRef.current?.removeEventListener('touchstart', handler);
      origRef.current?.removeEventListener('click', handler);
    };
  };

  return (
    <StyledCardCarousel
      data-component-name="m-ui-library-VanilaCardCarouselContainer"
      data-testid="ui-library-VanilaCardCarouselContainer"
      className={clsx(`col-12 py-3 px-0 ${styleclass}`)}
      {...customAttributes}
      variation={variation}
    >
      {isCombo() ? carouselComboHeader() : carouselHeader()}

      <div id={componentId} className={clsx(`glide ${isCombo() ? 'col-xl-8 p-0' : ''}`)}>
        <div className={clsx(!subHeaderText && !eyebrow ? 'glide__track__pt3' : 'glide__track')} data-glide-el="track">
          <ul className={clsx('glide__slides', 'pb-3')} id={`${componentId}__slides`}>
            {children}
          </ul>
        </div>
        {cardCount !== cardsPerView && (
          <div className="center-align">
            <div className={isCobrandVariant ? 'carouselControlType3b' : 'carouselControlType1'}>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx(
                    isCobrandVariant ? '' : 'glide__arrow',
                    `${
                      !isCobrandVariant && isDirectionRightToLeft
                        ? 'glide__arrow--right right-arrow'
                        : 'glide__arrow--left left-arrow'
                    }`
                  )}
                  id={`${componentId}_left_arrow`}
                  ref={leftArrowRef}
                  data-glide-dir={isCobrandVariant && isDirectionRightToLeft ? '>' : '<'}
                  aria-label="left-arrow"
                >
                  {isCobrandVariant && isDirectionRightToLeft ? 'Next' : ''}
                  <span
                    className={isCobrandVariant && isDirectionRightToLeft ? 'icon-arrow-right' : 'icon-arrow-left'}
                    id={`${componentId}_left_arrow`}
                    data-id="left"
                  ></span>
                  {isCobrandVariant && isDirectionRightToLeft ? '' : 'Previous'}
                </button>
              </div>

              <input
                type="hidden"
                id={`${componentId}-left-arrow-click-track`}
                ref={leftArrowClickTrackRef}
                className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
                data-custom_click_track_value={`${trackingProperties?.trackingContentPosition}|${trackingProperties?.leftArrowDesc}|internal`}
              />

              <div data-glide-el="controls[nav]">
                {Array.from({ length: cardCount - (cardsPerView - 1) }, (_, i) => (
                  <button className="" data-glide-dir={i} aria-label={`page-${i + 1}`} key={cardsPerView + i}></button>
                ))}
              </div>
              <div className="glide__arrows" data-glide-el="controls">
                <button
                  className={clsx(
                    isCobrandVariant ? '' : 'glide__arrow',
                    `${
                      !isCobrandVariant && isDirectionRightToLeft
                        ? 'glide__arrow--left left-arrow'
                        : 'glide__arrow--right right-arrow'
                    }`
                  )}
                  id={`${componentId}_right_arrow`}
                  ref={rightArrowRef}
                  data-glide-dir={isCobrandVariant && isDirectionRightToLeft ? '<' : '>'}
                  aria-label="click to show more"
                >
                  {isCobrandVariant && isDirectionRightToLeft ? '' : 'Next'}
                  <span
                    className={isCobrandVariant && isDirectionRightToLeft ? 'icon-arrow-left' : 'icon-arrow-right'}
                    id={`${componentId}_right_arrow`}
                    data-id="right"
                  ></span>
                  {isCobrandVariant && isDirectionRightToLeft ? 'Previous' : ''}
                </button>
              </div>

              <input
                type="hidden"
                id={`${componentId}-right-arrow-click-track`}
                ref={rightArrowClickTrackRef}
                className={clsx(`${trackingProperties?.clickTrack ? 'custom_click_track' : ''}`)}
                data-custom_click_track_value={`${trackingProperties?.trackingContentPosition}|${trackingProperties?.rightArrowDesc}|internal`}
              />
            </div>
          </div>
        )}
      </div>
    </StyledCardCarousel>
  );
};
